import { toArray } from '@assess/shared/type-utilities';

import { isPresent } from './is-present';
import { SearchBody, SearchBodyPaging, SearchBodySortFieldOrder } from '../types/search-body.types';

const defaultQuery = '*';
const defaultPaging: SearchBodyPaging = { current: 0 as const, size: 10 as const } as const;
const defaultSortOrder: SearchBodySortFieldOrder = 'asc';
const isValidSortOrder = (value: string | undefined): value is SearchBodySortFieldOrder =>
  ['asc', 'desc'].includes(value ?? '');

export const processSearchBody = (body: Partial<SearchBody> | undefined): SearchBody => {
  const processed: Partial<SearchBody> = {
    filters: processFilters(body?.filters),
    groupedFilters: processGroupedFilters(body?.groupedFilters),
    paging: processPaging(body?.paging),
    query: processQuery(body?.query),
    sortFields: processSortFields(body?.sortFields),
  } as any;

  return postProcess(processed);
};

const postProcess = (body: Partial<SearchBody>) => {
  return Object.entries(body)
    .filter(([_key, value]) => isPresent(value))
    .reduce((acc: SearchBody, entry) => {
      const [key, value] = entry;

      return { ...acc, [key]: value };
    }, {} as SearchBody) as SearchBody;
};

const processFilters = (filters: SearchBody['filters']) => {
  return Object.entries(filters ?? {})
    .filter(([key, _value]) => isPresent(key))
    .filter(([_key, value]) => isPresent(value))
    .reduce(
      (acc: Record<string, any>, entry) => {
        const [key, value] = entry;

        return { ...acc, [key]: value };
      },
      {} as Record<string, any>
    );
};

const processGroupedFilters = (groupedFilters: SearchBody['groupedFilters']) => {
  return toArray(groupedFilters)
    .filter((name) => !!name?.toString()?.trim())
    .join(',');
};

const processPaging = (paging: SearchBody['paging']) => {
  return {
    current: paging?.current ?? defaultPaging.current,
    size: paging?.size ?? defaultPaging.size,
  } as SearchBodyPaging;
};

const processQuery = (query: SearchBody['query']) => {
  return query?.trim() || defaultQuery;
};

const processSortFields = (sortFields: SearchBody['sortFields']) => {
  sortFields ??= [];

  return sortFields
    .filter((sortField) => !!sortField)
    .filter((sortField) => !!sortField.field?.trim())
    .map((sortField) => {
      const { field, order } = sortField;

      return {
        field,
        order: isValidSortOrder(order) ? order : defaultSortOrder,
      };
    });
};
